import { useState, FormEvent, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import ButtonWhatsApp from '@/components/base/ButtonWhatsApp';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { CONTACT } from '@/mocks/contactInfo';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { localBusiness, website, service, faqPage, breadcrumb, article } from '@/lib/seo';

const NUISIBLE_OPTIONS = [
  'Rats / souris',
  'Ravets / cafards',
  'Termites',
  'Punaises de lit',
  'Scolopendres',
  'Moustiques',
  'Fourmis',
  'Guêpes',
  'Autre / je ne sais pas',
];

const FORM_SUBMIT_URL = 'https://formspree.io/f/xyegzenq';

export default function Soumission() {
  useDocumentHead({
    title: 'Devis gratuit en 2 h — Gwada Nuisibles',
    description: 'Décrivez votre problème de nuisibles : rats, ravets, termites, punaises... Réponse avec prix ferme sous 2 h, partout en Guadeloupe. Gratuit.',
    jsonLd: breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Devis gratuit', path: '/soumission' }]),
  });

  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    const form = e.currentTarget;

    const gotchaEl = form.elements.namedItem('_gotcha') as HTMLInputElement | null;
    if (gotchaEl && gotchaEl.value.trim() !== '') {
      setError('');
      navigate('/merci');
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData(form);

      const resp = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      let parsed: { ok?: boolean; errors?: Array<{ message?: string }> } = {};
      try { parsed = await resp.json(); } catch { /* ignore */ }
      if (!resp.ok || parsed?.ok === false) {
        setError(parsed?.errors?.[0]?.message || 'Une erreur est survenue. Réessayez ou contactez-nous sur WhatsApp.');
        setSubmitting(false);
        return;
      }
      navigate('/merci');
    } catch {
      setError('Erreur réseau. Vérifiez votre connexion et réessayez.');
      setSubmitting(false);
    }
  };

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Devis gratuit'}]} />

      {/* HERO / INTRO */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/soumission-1.webp"
            alt="Technicien Gwada Nuisibles consultant un smartphone pour un diagnostic photo WhatsApp dans un jardin tropical en Guadeloupe"
            className="w-full h-full object-cover object-top"
           loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,58,46,0.94), rgba(11,58,46,0.74) 52%, rgba(11,58,46,0.42))' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,58,46,0.55), transparent 38%, transparent 82%, rgba(11,58,46,0.38))' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-3xl md:text-5xl text-white leading-tight mb-6">
              Votre devis gratuit en 2 h
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-6 max-w-2xl">
              Décrivez votre problème (une photo aide beaucoup) : un technicien vous répond sous 2 h ouvrées avec un prix ferme. Pas de démarchage, pas d&rsquo;engagement.
            </p>
            <ButtonWhatsApp>Nous écrire sur WhatsApp</ButtonWhatsApp>
          </div>
        </div>
      </section>

      {/* FORMULAIRE */}
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="bg-background-100 rounded-2xl p-6 md:p-10 border border-background-200/70"
          >
            {/* Nuisible */}
            <div className="mb-6">
              <label htmlFor="nuisible" className="block text-foreground-800 text-sm font-semibold mb-2">
                Quel nuisible ?
              </label>
              <select
                id="nuisible"
                name="nuisible"
                className="w-full px-4 py-3 rounded-lg border border-background-300/70 bg-white text-foreground-800 text-sm focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-500/20 transition-colors"
              >
                <option value="">— Sélectionnez —</option>
                {NUISIBLE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Commune */}
            <div className="mb-6">
              <label htmlFor="commune" className="block text-foreground-800 text-sm font-semibold mb-2">
                Votre commune
              </label>
              <input
                type="text"
                id="commune"
                name="commune"
                placeholder="Ex : Les Abymes, Baie-Mahault, Le Gosier..."
                className="w-full px-4 py-3 rounded-lg border border-background-300/70 bg-white text-foreground-800 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-500/20 transition-colors"
              />
            </div>

            {/* Description */}
            <div className="mb-6">
              <label htmlFor="description" className="block text-foreground-800 text-sm font-semibold mb-2">
                Décrivez le problème
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                maxLength={500}
                placeholder="Depuis quand ? Où ? Combien en voyez-vous ?"
                className="w-full px-4 py-3 rounded-lg border border-background-300/70 bg-white text-foreground-800 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-500/20 transition-colors resize-none"
              ></textarea>
            </div>

            {/* Nom */}
            <div className="mb-6">
              <label htmlFor="nom" className="block text-foreground-800 text-sm font-semibold mb-2">
                Nom
              </label>
              <input
                type="text"
                id="nom"
                name="nom"
                className="w-full px-4 py-3 rounded-lg border border-background-300/70 bg-white text-foreground-800 text-sm focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-500/20 transition-colors"
              />
            </div>

            {/* Téléphone (obligatoire) */}
            <div className="mb-6">
              <label htmlFor="telephone" className="block text-foreground-800 text-sm font-semibold mb-2">
                Téléphone <span className="text-accent-500">*</span>
              </label>
              <input
                type="tel"
                id="telephone"
                name="telephone"
                required
                placeholder="0690 00 00 00"
                className="w-full px-4 py-3 rounded-lg border border-background-300/70 bg-white text-foreground-800 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-500/20 transition-colors"
              />
            </div>

            {/* Email */}
            <div className="mb-6">
              <label htmlFor="email" className="block text-foreground-800 text-sm font-semibold mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="vous@exemple.com"
                className="w-full px-4 py-3 rounded-lg border border-background-300/70 bg-white text-foreground-800 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-500/20 transition-colors"
              />
            </div>

            {/* Hidden source */}
            <input type="hidden" name="source" value="gwada-soumission" />

            {/* Anti-spam honeypot */}
            <input
              type="text"
              name="_gotcha"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="form-honeypot"
            />

            {/* Erreur */}
            {error && (
              <div className="mb-6 p-4 rounded-lg bg-accent-100 text-accent-900 text-sm border border-accent-300">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 rounded-lg bg-accent-500 text-white font-heading font-bold text-base hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Envoi en cours...' : 'Recevoir mon prix ferme'}
            </button>

            <p className="text-foreground-400 text-xs text-center mt-4 leading-relaxed">
              Réponse sous 2 h ouvrées. Vos données ne servent qu&rsquo;à vous répondre — jamais revendues.
            </p>
          </form>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}